import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { EngineTheme, useMergeClasses, useTheme } from '../../../../../../theme';
import Money from '../../../../../../Money';
import { Money as MoneyType } from '../../../../../../Money/types';
import { ServiceType } from '../../../../../../CardExtra/types';
import { SafeHtml } from '../../../SafeHtml';

interface Props {
	className?: string;
	classes?: EngineTheme['Shared']['CardExtraView']['CardExtraView'];

	// common
	type: ServiceType;
	selected?: boolean;
	icon?: React.ReactNode;
	title?: React.ReactNode;
	badge?: React.ReactNode;
	description?: React.ReactNode;
	content?: React.ReactNode;

	// styles
	styles?: {
		background?: string;
		order?: number;
	};

	// prices
	minPrice?: MoneyType;
	totalPrice?: MoneyType;

	// controls
	headerControls?: React.ReactNode;
	additionalControls?: React.ReactNode;
	footerControls?: React.ReactNode;

	// handlers
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const CardExtraView: React.FC<Props> = ({
	className,
	classes,
	type,
	selected,
	icon,
	title,
	badge,
	description,
	content,
	styles,
	minPrice,
	totalPrice,
	headerControls,
	additionalControls,
	footerControls,
	onClick
}) => {
	const { t } = useTranslation('CardExtra');
	const { CardExtraView: defaultClasses } = useTheme('Shared').CardExtraView;

	const theme = useMergeClasses(defaultClasses, classes);

	const descriptionContent =
		description && typeof description === 'string' ? (
			<SafeHtml className={theme.description} html={description} />
		) : (
			<div className={theme.description}>{description}</div>
		);

	const existTotalPrice = !!totalPrice && totalPrice.amount !== undefined;

	const showContent = !!content || !!descriptionContent;
	const showPrice = !!totalPrice || !!minPrice;
	const showFooter = showPrice || !!footerControls;

	return (
		<div
			className={cn(
				theme.root,
				{
					[theme.root_selected]: selected,
					[theme.root_clickable]: !!onClick,
					[theme.root_seats]: type === 'seats',
					[theme.root_baggage]: type === 'baggage',
					[theme.root_baggageDelivery]: type === 'baggage-delivery',
					[theme.root_animal]: type === 'animal',
					[theme.root_meal]: type === 'meal',
					[theme.root_businessLounge]: type === 'business-lounge',
					[theme.root_priorityBoarding]: type === 'priority-boarding',
					[theme.root_insurance]: type === 'insurance',
					[theme.root_transfer]: type === 'transfer',
					[theme.root_capsuleHotel]: type === 'capsule-hotel',
					[theme.root_loyalty]: type === 'loyalty',
					[theme.root_upgrade]: type === 'upgrade',
					[theme.root_sms]: type === 'sms',
					[theme.root_refundableTicket]: type === 'refundable-ticket',
					[theme.root_misc]: type === 'misc'
				},
				className
			)}
			style={{ order: styles?.order }}
			data-service-type={type}
			data-test-id="card-extra"
			onClick={onClick}
		>
			<div className={theme.header} style={{ background: styles?.background }}>
				{title && <div className={theme.header__title}>{title}</div>}
				{icon && <div className={theme.header__icon}>{icon}</div>}
				{badge && <div className={theme.header__badge}>{badge}</div>}

				{headerControls}
			</div>

			{showContent && <div className={theme.content}>{content ?? descriptionContent}</div>}

			{additionalControls && <div className={theme.controls}>{additionalControls}</div>}

			{showFooter && (
				<div className={theme.footer}>
					{showPrice && (
						<span className={theme.footer__price}>
							{t(existTotalPrice ? 'total' : 'from')}
							{existTotalPrice ? ':' : ''}
							<Money money={existTotalPrice ? totalPrice : minPrice} />
						</span>
					)}

					{footerControls}
				</div>
			)}
		</div>
	);
};

export default CardExtraView;
